import { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Button, IconButton } from '@upmc/ims.component-library';

import axiosInstance from '../../../../services/axios/axios';
import { UserContext } from 'context/User/UserContext';
import {
	getImpersonationData,
	resetImpersonationSession,
	setImpersonationSession,
} from './_utils';
import { deepCloneObject } from '../../../../store/utility';
import WithLoadingHandler from '../../../../hoc/withLoadingHandler/withLoadingHandler';
import { Tooltip } from '../../../UI/MDC/Tooltip/Tooltip';
import Badge from '@mui/material/Badge';
import PersonSearch from '../../../UI/Input/PersonSearch/PersonSearch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Subtitle1 } from '../../../UI/MDC/Typography/Typography';
import CoPresent from '@mui/icons-material/CoPresent';
import Edit from '@mui/icons-material/Edit';
import { CONTENT_ALIGNMENT } from '../../../UI/MDC/Tooltip/_types';
import { KEY_SESSION } from './_constants';
import { CSS_CLASSES } from '../../_constants';

import classes from './ImpersonationIcon.module.scss';
import classesIcon from '../Icon.module.scss';

export const ImpersonationIcon = ({ align = CONTENT_ALIGNMENT.BOTTOM }) => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const dispatch = useDispatch();

	const [sIsDialogOpen, setIsDialogOpen] = useState(false);
	const [sErrorMessage, setErrorMessage] = useState('');
	const [sIsLoading, setIsLoading] = useState(false);
	const [sIsSearching, setIsSearching] = useState(false);
	const [sRows, setRows] = useState([]);
	const [sTargetUser, setTargetUser] = useState(
		contextUser.userInfo?.impersonationProfile?.Name
			? contextUser.userInfo.impersonationProfile.Name.replace(',', ', ')
			: null
	);

	/**
	 * Helper method filtering rows stored in local state against user input. Adds a property to row objects
	 * used to determine visibility of row.
	 *
	 * @param {Event} e event object raised on input change.
	 * @returns void
	 */
	const indexRows = e => {
		let rowsCopy = deepCloneObject(sRows);
		rowsCopy.forEach(row => {
			const indexed = Object.keys(row.Cells).some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(e.target.value)
			);
			row.isDisplayed = indexed;
		});
		setRows(rowsCopy);
	};

	/**
	 * Asynchronous handler raised on user action. Resets all session variables affected by impersonation.
	 *
	 * @returns void
	 */
	const onResetHandler = async () => {
		setIsLoading(true);
		try {
			await resetImpersonationSession(
				contextUser.userInfo.userProfile.ESOID
			);
		} catch {
		} finally {
			setTargetUser(null);
			setIsDialogOpen(false);
			setIsLoading(false);

			contextUser.setUserInfo(userInfo => {
				return { ...userInfo, impersonationProfile: null };
			});
			sessionStorage.removeItem(KEY_SESSION);
		}
	};

	/**
	 * Asynchronous handler raised on user action. Sets session variables according to selected user information.
	 *
	 * @param {Object} packet object representing clicked row object.
	 *
	 * @returns void
	 */
	const onRowClickedHandler = async packet => {
		setIsLoading(true);
		const identifier =
			packet.payload.Cells?.['Network_ID']?.Value ??
			packet.payload.Cells?.['UPMC_E-Sign-On_ID']?.Value;
		const userImpersonation = await getImpersonationData(
			identifier,
			dispatch
		);

		try {
			await setImpersonationSession(packet.payload.Cells);
		} catch {
		} finally {
			if (!!userImpersonation) {
				setTargetUser(userImpersonation.Name.replace(',', ', '));
				contextUser.setUserInfo(userInfo => {
					return {
						...userInfo,
						impersonationProfile: userImpersonation,
					};
				});
				sessionStorage.setItem(KEY_SESSION, identifier);
			}
			setIsLoading(false);
		}
	};

	return (
		<>
			<Tooltip align={align} content='Impersonation'>
				<IconButton
					className={classesIcon[CSS_CLASSES.ICON]}
					color='inherit'
					id='icon--impersonation'
					onClick={() => setIsDialogOpen(!sIsDialogOpen)}
					size='large'
				>
					{sTargetUser ? (
						<Badge variant='dot' color='error'>
							<CoPresent />
						</Badge>
					) : (
						<CoPresent />
					)}
				</IconButton>
			</Tooltip>
			<Dialog
				open={sIsDialogOpen}
				onClose={() => {
					setIsLoading(false);
					setIsDialogOpen(false);
				}}
			>
				<DialogTitle>User Impersonation</DialogTitle>
				<WithLoadingHandler
					isLoading={sIsLoading}
					showChildren={!sIsLoading}
				>
					<DialogContent className={classes['dialog__content']}>
						{sTargetUser ? (
							<>
								<Subtitle1>
									<strong>Impersonating: </strong>
									{sTargetUser}
								</Subtitle1>
								<Tooltip align={align} content='Edit'>
									<IconButton
										onClick={() => {
											setTargetUser(null);
										}}
									>
										<Edit />
									</IconButton>
								</Tooltip>
							</>
						) : (
							<PersonSearch
								className={classes['person-search']}
								dataTable={{
									onTextFieldChangedHandler: indexRows,
									onRowClickedHandler: onRowClickedHandler,
									rows: sRows.filter(
										row => row?.isDisplayed !== false
									),
								}}
								errorMessage={sErrorMessage}
								isLoading={sIsSearching}
								multiPerson={false}
								onButtonClickedHandler={payload => {
									setIsSearching(true);

									axiosInstance
										.post(
											'api/Components/PersonSearch',
											payload
										)
										.then(response => {
											switch (response.status) {
												case 200:
													switch (
														response.data
															.responseCode
													) {
														case 200:
															setRows(
																response.data
																	.payload
															);
															setErrorMessage('');
															break;
														default:
															setErrorMessage(
																response.data
																	.messages[0]
																	.Value
															);
													}
													break;
												default:
													break;
											}
										})
										.catch(error => {
											setErrorMessage(
												error &&
													error.response &&
													error.response.data
													? error.response.data
															.payload
													: error.message
											);
										})
										.finally(() => {
											setIsSearching(false);
										});
								}}
								requestTarget='REQUESTEE'
							/>
						)}
					</DialogContent>

					{sTargetUser ? (
						<DialogActions>
							<Button variant='text' onClick={onResetHandler}>
								Reset
							</Button>
							<Button
								autoFocus
								onClick={() => setIsDialogOpen(false)}
								variant='text'
							>
								Close
							</Button>
						</DialogActions>
					) : null}
				</WithLoadingHandler>
			</Dialog>
		</>
	);
};
